import { IncomingHttpHeaders, IncomingMessage } from 'http';
import { NextRequest } from 'next/server';

import { EMPTY_STR, SESSION_JWT_HEADER } from '../constants/common';

/** Minimal request shape for header reads (full `IncomingMessage`, Next.js `NextRequest`, or headers-only). */
export type RequestWithHeaders = IncomingMessage | NextRequest | Pick<IncomingMessage, 'headers'>;

function extractRequestHeaders(request: RequestWithHeaders): Record<string, string> {
    // For NextRequest (Edge API Routes, Middleware, App Router)
    if ('headers' in request && typeof request.headers.get === 'function') {
        const headersObj: Record<string, string> = {};
        for (const [key, value] of (request.headers as Headers).entries()) {
            headersObj[key.toLowerCase()] = value;
        }
        return headersObj;
    }
    // For IncomingMessage (Pages Router, Node.js)
    if ('headers' in request && typeof request.headers === 'object') {
        const headers = request.headers as IncomingHttpHeaders;
        const headersObj: Record<string, string> = {};
        Object.keys(headers).forEach((key) => {
            const value = headers[key];
            if (typeof value === 'string') {
                headersObj[key.toLowerCase()] = value;
            } else if (Array.isArray(value)) {
                headersObj[key.toLowerCase()] = value.join(', ');
            }
        });
        return headersObj;
    }
    return {};
}

function getHeaderValue(
    request: RequestWithHeaders,
    headerName: string,
    fallback: string = EMPTY_STR,
): string {
    const headers = extractRequestHeaders(request);
    return headers[headerName.toLowerCase()] ?? fallback;
}

function getSessionJwtHeader(request: RequestWithHeaders): string | undefined {
    return getHeaderValue(request, SESSION_JWT_HEADER).trim() || undefined;
}

// Referers that must never become the post-sign-in destination: the site root, and the
// sign-in pages (they redirect signed-in subscribers to the referer, so they would loop).
const EXCLUDED_REFERER_PATHS: (string | RegExp)[] = [
    '/',
    /^(?:\/[^/]+)?\/(?:account\/(?:signin|login|sign-in|check-email)|tv\/signin)(?:\/|$)/i,
];

const isExcludedRefererPath = (pathname: string): boolean =>
    EXCLUDED_REFERER_PATHS.some((path) =>
        typeof path === 'string' ? path === pathname.toLowerCase() : path.test(pathname),
    );

function getValidReferer(request: IncomingMessage | NextRequest): string {
    const referer = getHeaderValue(request, 'referer', EMPTY_STR);

    if (!referer) {
        return EMPTY_STR;
    }

    const forwardedHost = getHeaderValue(request, 'x-forwarded-host', EMPTY_STR).toLowerCase();
    try {
        const refererUrl = new URL(referer);
        if (
            refererUrl.hostname.toLowerCase() === forwardedHost &&
            !isExcludedRefererPath(refererUrl.pathname)
        ) {
            return referer;
        }
    } catch (e) {
        // Invalid referer URL, fall through to return EMPTY_STR
    }
    return EMPTY_STR;
}

/**
 * Validates redirectUrl: only relative paths are allowed (starts with /, not root, not protocol-relative).
 * Rejects backslashes and URL-encoded backslashes to prevent redirect bypass. Absolute URLs are rejected.
 */
function getValidRedirectUrl(redirectUrl: string | null): string {
    if (!redirectUrl) {
        return EMPTY_STR;
    }
    if (redirectUrl.includes('\\')) {
        return EMPTY_STR;
    }
    try {
        if (decodeURIComponent(redirectUrl).includes('\\')) {
            return EMPTY_STR;
        }
    } catch {
        return EMPTY_STR;
    }
    const isValid =
        redirectUrl.startsWith('/') && redirectUrl !== '/' && !redirectUrl.startsWith('//');
    return isValid ? redirectUrl : EMPTY_STR;
}

export {
    extractRequestHeaders,
    getHeaderValue,
    getSessionJwtHeader,
    getValidRedirectUrl,
    getValidReferer,
};
